import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ITableProps, ITableData, ICTTableCol, ICTTableRow, DeltaOf } from '@tencent/slide-sdk-model';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { canvasToEmu, emuToCanvas, ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import _set from 'lodash/set';

export const MIN_ROW_HEIGHT = ptToCanvas(30);
export const MIN_COL_WIDTH = ptToCanvas(20);

/**
 * 表格独有属性
 */
export class TableInfoConverter {
    /**
     * 表格独有属性的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ITableProps>>,
        uiData: ITableModel,
    ): void {
        // 表格列宽信息
        TableInfoConverter.convertColWidthDataSdkToUi(sdkData, uiData);
        // 表格行高信息
        TableInfoConverter.convertRowHeightDataSdkToUi(sdkData, uiData);
    }

    /**
     * 表格独有属性的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITableModel>>,
        sdkData: DeltaOf<ITableData>,
    ): void {
        // 表格列宽信息
        TableInfoConverter.convertColWidthDataUiToSdk(uiData, sdkData);
        // 表格行高信息
        TableInfoConverter.convertRowHeightDataUiToSdk(uiData, sdkData);
    }

    /**
     * 表格列宽列表
     * @param sdkData
     * @param uiData
     */
    private static convertColWidthDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ITableProps>>,
        uiData: ITableModel,
    ): void {
        const tblColWidths: number[] = [];
        let totalWidth = 0;
        // 将sdk的列宽数组转换到ui上
        const tblColList = sdkData?.graphic?.graphicData?.tbl?.tblGrid?.gridCol || [];
        tblColList.forEach((colInfo: any) => {
            const colWidth = Math.max(Math.floor(emuToCanvas(colInfo.w)), MIN_COL_WIDTH);
            tblColWidths.push(colWidth);
            totalWidth += colWidth;
        });

        // 历史逻辑：因为sdk列宽是浮点数而ui列宽是向下取整的，所以会和表格总宽对不上，将多出来的均摊到前面几列中
        for (let col = 0; col < uiData.props.style.width - totalWidth && col < tblColWidths.length; col++) {
            tblColWidths[col] += 1;
        }
        uiData.props.colWidths = tblColWidths;
    }

    /**
     * 表格行高列表
     * @param sdkData
     * @param uiData
     */
    private static convertRowHeightDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ITableProps>>,
        uiData: ITableModel,
    ): void {
        const tblRowHeights: number[] = [];
        const sdkTblRowList = sdkData?.graphic?.graphicData?.tbl?.tr || [];
        sdkTblRowList.forEach((rowInfo: any) => {
            const rowHeight = Math.round(emuToCanvas(rowInfo.h));
            tblRowHeights.push(rowHeight);
        });
        uiData.props.rowHeights = tblRowHeights;
    }

    /**
     * 表格列宽列表
     * @param uiData
     * @param sdkData
     */
    private static convertColWidthDataUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITableModel>>,
        sdkData: DeltaOf<ITableData>,
    ): void {
        if (!uiData.props?.colWidths?.length) {
            return;
        }
        const gridColArr: (ICTTableCol | undefined)[] = [];
        uiData.props.colWidths.forEach((colWidth) => {
            if (colWidth === undefined) {
                gridColArr.push(undefined);
            } else {
                gridColArr.push({ w: canvasToEmu(colWidth) });
            }
        });
        _set(sdkData, 'graphic.graphicData.tbl.tblGrid.gridCol', gridColArr);
    }

    /**
     * 表格行高列表
     * @param uiData
     * @param sdkData
     */
    private static convertRowHeightDataUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITableModel>>,
        sdkData: DeltaOf<ITableData>,
    ): void {
        if (!uiData.props?.rowHeights?.length) {
            return;
        }
        // 组合出带有行高的行信息
        const trArr: (ICTTableRow | undefined)[] = [];
        uiData.props.rowHeights.forEach((rowHeight) => {
            if (rowHeight === undefined) {
                trArr.push(undefined);
            } else {
                trArr.push({ h: canvasToEmu(rowHeight) });
            }
        });

        // 如果已存在行信息，则merge
        const existTrArr = sdkData?.graphic?.graphicData?.tbl?.tr;
        if (existTrArr?.length) {
            trArr.forEach((tr, index) => {
                trArr[index] = Object.assign({}, tr, existTrArr[index]);
            });
        } else {
            _set(sdkData, 'graphic.graphicData.tbl.tr', trArr);
        }
    }
}
